import { useEffect, useRef, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [pathname, hash]);
  return null;
};

// Layout (eager - needed on every page)
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import NewsletterCTA from "@/components/NewsletterCTA";

// Minimal loading fallback
const PageLoader = () => (
  <div style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:'40vh'}}>
    <div style={{width:32,height:32,border:'3px solid #e5e7eb',borderTopColor:'#1e3a5f',borderRadius:'50%',animation:'spin 0.6s linear infinite'}} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

// Public Pages (lazy loaded)
const HomePage = lazy(() => import("@/pages/HomePage"));
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const WorkforcePage = lazy(() => import("@/pages/WorkforcePage"));
const VolunteerPage = lazy(() => import("@/pages/VolunteerPage"));
const CommunityCollaborationPage = lazy(() => import("@/pages/CommunityCollaborationPage"));
const EventSpotlightPage = lazy(() => import("@/pages/EventSpotlightPage"));
const CollaborativeDetailPage = lazy(() => import("@/pages/CollaborativeDetailPage"));
const HealthWellnessPage = lazy(() => import("@/pages/HealthWellnessPage"));
const HousingStabilityPage = lazy(() => import("@/pages/HousingStabilityPage"));
const PartnerDirectoryPage = lazy(() => import("@/pages/PartnerDirectoryPage"));
const EventsPage = lazy(() => import("@/pages/EventsPage"));
const NewsPage = lazy(() => import("@/pages/NewsPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const TestimonialsPage = lazy(() => import("@/pages/TestimonialsPage"));
const ResourceLibraryPage = lazy(() => import("@/pages/ResourceLibraryPage"));
const WorkshopCatalogPage = lazy(() => import("@/pages/WorkshopCatalogPage"));
const WorkshopDetailPage = lazy(() => import("@/pages/WorkshopDetailPage"));
const InstructorDirectoryPage = lazy(() => import("@/pages/InstructorDirectoryPage"));
const InstructorProfilePage = lazy(() => import("@/pages/InstructorProfilePage"));
const VideoRoomPage = lazy(() => import("@/pages/VideoRoomPage"));
const MyAccountPage = lazy(() => import("@/pages/MyAccountPage"));
const InstructorPortalPage = lazy(() => import("@/pages/InstructorPortalPage"));
const PackagesPage = lazy(() => import("@/pages/PackagesPage"));
const CoachingPage = lazy(() => import("@/pages/CoachingPage"));
const BookSessionPage = lazy(() => import("@/pages/BookSessionPage"));
const ReferralLandingPage = lazy(() => import("@/pages/ReferralLandingPage"));
const AppointmentBookingPage = lazy(() => import("@/pages/AppointmentBookingPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const DonatePage = lazy(() => import("@/pages/DonatePage"));
const DonateSuccessPage = lazy(() => import("@/pages/DonateSuccessPage"));
const SponsorshipPage = lazy(() => import("@/pages/SponsorshipPage"));
const SponsorPortalLogin = lazy(() => import("@/pages/sponsor/SponsorPortalLogin"));
const SponsorDashboard = lazy(() => import("@/pages/sponsor/SponsorDashboard"));
const SponsorForgotPassword = lazy(() => import("@/pages/sponsor/SponsorForgotPassword"));
const SponsorResetPassword = lazy(() => import("@/pages/sponsor/SponsorResetPassword"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));

// Impact Calendar Pages (lazy loaded)
const ImpactCalendarPage = lazy(() => import("@/pages/ImpactCalendarPage"));
const EventSubmitPage = lazy(() => import("@/pages/EventSubmitPage"));
const EventDetailPage = lazy(() => import("@/pages/EventDetailPage"));
const AdSubmitPage = lazy(() => import("@/pages/AdSubmitPage"));
const GalleriesPage = lazy(() => import("@/pages/GalleriesPage"));
const PartnerDashboardPage = lazy(() => import("@/pages/PartnerDashboardPage"));
const PartnerOnboardingPage = lazy(() => import("@/pages/PartnerOnboardingPage"));
const CalendarMapPage = lazy(() => import("@/pages/CalendarMapPage"));
const ApiDocsPage = lazy(() => import("@/pages/ApiDocsPage"));

// Admin Pages (lazy loaded)
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AuthCallback = lazy(() => import("@/pages/admin/AuthCallback"));
const VolunteerAdminPage = lazy(() => import("@/pages/admin/VolunteerAdminPage"));
const AdminPhase3Page = lazy(() => import("@/pages/admin/AdminPhase3Page"));

// Volunteer Infrastructure Pages (lazy loaded)
const VolunteerPortalPage = lazy(() => import("@/pages/volunteer/VolunteerPortalPage"));
const VolunteerRegisterPage = lazy(() => import("@/pages/volunteer/VolunteerRegisterPage"));
const VolunteerLoginPage = lazy(() => import("@/pages/volunteer/VolunteerLoginPage"));
const VolunteerDashboardPage = lazy(() => import("@/pages/volunteer/VolunteerDashboardPage"));
const VolunteerLeaderboardPage = lazy(() => import("@/pages/volunteer/VolunteerLeaderboardPage"));
const OpportunityDetailPage = lazy(() => import("@/pages/volunteer/OpportunityDetailPage"));
const TrainingHubPage = lazy(() => import("@/pages/volunteer/TrainingHubPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/volunteer/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/volunteer/ResetPasswordPage"));

// Organization Pages (lazy loaded)
const OrgRegisterPage = lazy(() => import("@/pages/organization/OrgRegisterPage"));
const OrgLoginPage = lazy(() => import("@/pages/organization/OrgLoginPage"));
const OrgDashboardPage = lazy(() => import("@/pages/organization/OrgDashboardPage"));

// Partner Pages (lazy loaded)
const SubscriptionPage = lazy(() => import("@/pages/partner/SubscriptionPage"));
const PartnerLoginPage = lazy(() => import("@/pages/partner/PartnerLoginPage"));
const PartnerRegisterPage = lazy(() => import("@/pages/partner/PartnerRegisterPage"));
const UnifiedPartnerDashboard = lazy(() => import("@/pages/partner/PartnerDashboardPage"));

const UnsubscribePage = lazy(() => import("@/pages/UnsubscribePage"));
const EmailPreferencesPage = lazy(() => import("@/pages/EmailPreferencesPage"));
// Corporate Team Pages (lazy loaded)
const CorporateRegisterPage = lazy(() => import("@/pages/corporate/CorporateRegisterPage"));
const CorporateLoginPage = lazy(() => import("@/pages/corporate/CorporateLoginPage"));
const CorporateDashboardPage = lazy(() => import("@/pages/corporate/CorporateDashboardPage"));

// Events & Webinar Management System (lazy loaded)
const EventsManagement = lazy(() => import("@/pages/EventsManagement"));
const EventCreate = lazy(() => import("@/pages/EventCreate"));
const EventDetail = lazy(() => import("@/pages/EventDetail"));
const PublicEventPage = lazy(() => import("@/pages/PublicEventPage"));
const SmartFormBuilder = lazy(() => import("@/pages/SmartFormBuilder"));
const MyEventsPage = lazy(() => import("@/pages/MyEvents"));
const MyDashboardPage = lazy(() => import("@/pages/MyDashboardPage"));
const EventCalendarView = lazy(() => import("@/pages/EventCalendarView"));
const EventCheckIn = lazy(() => import("@/pages/EventCheckIn"));
const EventReports = lazy(() => import("@/pages/EventReports"));
const WebinarStudio = lazy(() => import("@/pages/WebinarStudio"));
const WebinarRoom = lazy(() => import("@/pages/WebinarRoom"));
const WebinarJoin = lazy(() => import("@/pages/WebinarJoin"));
const CreatorBranding = lazy(() => import("@/pages/CreatorBranding"));
const EvergreenPlayer = lazy(() => import("@/pages/EvergreenPlayer"));
const FormTemplateGallery = lazy(() => import("@/pages/FormTemplateGallery"));

// Auth Context
import { AuthProvider, useAuth } from "@/context/AuthContext";

const BACKEND_URL = '';
export const API = `${BACKEND_URL}/api`;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // If user data passed from AuthCallback, skip check
    if (location.state?.user) {
      setIsChecking(false);
      return;
    }

    const verifyAuth = async () => {
      const isAuthenticated = await checkAuth();
      if (!isAuthenticated) {
        navigate("/admin/login", { replace: true });
      }
      setIsChecking(false);
    };

    verifyAuth();
  }, [checkAuth, navigate, location.state]);

  if (loading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-gray-light">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mx-auto"></div>
          <p className="mt-4 text-brand-blue font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? children : null;
};

import SEOHead from "@/components/SEOHead";

// 404 Page
const NotFoundPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-brand-blue mb-4">404</h1>
        <p className="text-xl text-gray-600 mb-8">Page not found</p>
        <button 
          onClick={() => navigate("/")}
          className="btn-primary"
          data-testid="go-home-btn"
        >
          Go Home
        </button>
      </div>
    </div>
  );
};

// Public Layout
const PublicLayout = ({ children }) => {
  const location = useLocation();
  const hideNewsletterCTA = ['/', '/impact-calendar', '/admin', '/volunteer/register', '/partner/register', '/corporate/register'].some(
    p => location.pathname === p || location.pathname.startsWith('/admin')
  );
  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead path={location.pathname} />
      <Header />
      <main className="flex-grow">{children}</main>
      {!hideNewsletterCTA && <NewsletterCTA />}
      <Footer />
    </div>
  );
};

// App Router - Handle OAuth callback BEFORE routes
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check for session_id in hash synchronously during render
  // This prevents race conditions with auth checks
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
      <Route path="/welcome" element={<PublicLayout><LandingPage /></PublicLayout>} />
      <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
      <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
      <Route path="/services/workforce" element={<PublicLayout><WorkforcePage /></PublicLayout>} />
      <Route path="/services/volunteer" element={<PublicLayout><VolunteerPage /></PublicLayout>} />
      <Route path="/services/collaboration" element={<PublicLayout><CommunityCollaborationPage /></PublicLayout>} />
      <Route path="/services/collaboration/:slug" element={<PublicLayout><CollaborativeDetailPage /></PublicLayout>} />
      <Route path="/services/health" element={<PublicLayout><HealthWellnessPage /></PublicLayout>} />
      <Route path="/services/housing" element={<PublicLayout><HousingStabilityPage /></PublicLayout>} />
      <Route path="/partners" element={<PublicLayout><PartnerDirectoryPage /></PublicLayout>} />
      <Route path="/events" element={<PublicLayout><EventsPage /></PublicLayout>} />
      <Route path="/events/:eventId" element={<PublicLayout><EventsPage /></PublicLayout>} />
      <Route path="/news" element={<PublicLayout><NewsPage /></PublicLayout>} />
      <Route path="/news/:slug" element={<PublicLayout><NewsPage /></PublicLayout>} />
      <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} />
      <Route path="/blog/:slug" element={<PublicLayout><BlogPage /></PublicLayout>} />
      <Route path="/testimonials" element={<PublicLayout><TestimonialsPage /></PublicLayout>} />
      <Route path="/resources" element={<PublicLayout><ResourceLibraryPage /></PublicLayout>} />
      <Route path="/workshops" element={<Navigate to="/events" replace />} />
      <Route path="/workshops/:workshopId" element={<PublicLayout><WorkshopDetailPage /></PublicLayout>} />
      <Route path="/instructors" element={<PublicLayout><InstructorDirectoryPage /></PublicLayout>} />
      <Route path="/instructors/:instructorId" element={<PublicLayout><InstructorProfilePage /></PublicLayout>} />
      <Route path="/video-room/:roomId" element={<VideoRoomPage />} />
      <Route path="/account" element={<PublicLayout><MyAccountPage /></PublicLayout>} />
      <Route path="/instructor-portal" element={<PublicLayout><InstructorPortalPage /></PublicLayout>} />
      <Route path="/packages" element={<PublicLayout><PackagesPage /></PublicLayout>} />
      <Route path="/coaching" element={<PublicLayout><CoachingPage /></PublicLayout>} />
      <Route path="/instructors/:instructorId/book" element={<PublicLayout><BookSessionPage /></PublicLayout>} />
      <Route path="/referral/:code" element={<ReferralLandingPage />} />
      <Route path="/appointments" element={<PublicLayout><AppointmentBookingPage /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
      <Route path="/donate" element={<PublicLayout><DonatePage /></PublicLayout>} />
      <Route path="/donate/success" element={<PublicLayout><DonateSuccessPage /></PublicLayout>} />
      <Route path="/sponsorship" element={<PublicLayout><SponsorshipPage /></PublicLayout>} />
      
      {/* Sponsor Portal */}
      <Route path="/sponsor-portal" element={<SponsorPortalLogin />} />
      <Route path="/sponsor-portal/dashboard" element={<SponsorDashboard />} />
      <Route path="/sponsor-portal/forgot-password" element={<SponsorForgotPassword />} />
      <Route path="/sponsor-portal/reset-password" element={<SponsorResetPassword />} />

      <Route path="/privacy" element={<PublicLayout><PrivacyPage /></PublicLayout>} />
      <Route path="/terms" element={<PublicLayout><TermsPage /></PublicLayout>} />
      
      {/* Developer API Docs */}
      <Route path="/developers" element={<ApiDocsPage />} />

      {/* Impact Calendar Routes */}
      <Route path="/impact-calendar" element={<ImpactCalendarPage />} />
      <Route path="/impact-calendar/spotlight" element={<EventSpotlightPage />} />
      <Route path="/impact-calendar/:regionSlug" element={<ImpactCalendarPage />} />
      <Route path="/impact-calendar/submit-event" element={<EventSubmitPage />} />
      <Route path="/impact-calendar/advertise" element={<PublicLayout><AdSubmitPage /></PublicLayout>} />
      <Route path="/impact-calendar/event/:eventId" element={<EventDetailPage />} />
      <Route path="/impact-calendar/galleries" element={<GalleriesPage />} />
      <Route path="/impact-calendar/galleries/:galleryId" element={<GalleriesPage />} />
      <Route path="/impact-calendar/map" element={<CalendarMapPage />} />
      <Route path="/impact-calendar/partner" element={<PartnerDashboardPage />} />
      <Route path="/impact-calendar/partner/onboarding" element={<PartnerOnboardingPage />} />
      
      {/* Events & Webinar Management */}
      <Route path="/manage/events" element={<ProtectedRoute><EventsManagement /></ProtectedRoute>} />
      <Route path="/manage/events/create" element={<ProtectedRoute><EventCreate /></ProtectedRoute>} />
      <Route path="/manage/events/:eventId" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
      <Route path="/manage/events/:eventId/edit" element={<ProtectedRoute><EventCreate /></ProtectedRoute>} />
      <Route path="/manage/events/:eventId/forms/new" element={<ProtectedRoute><SmartFormBuilder /></ProtectedRoute>} />
      <Route path="/manage/events/:eventId/forms/:formId" element={<ProtectedRoute><SmartFormBuilder /></ProtectedRoute>} />
      <Route path="/manage/events/:eventId/check-in" element={<ProtectedRoute><EventCheckIn /></ProtectedRoute>} />
      <Route path="/manage/events/:eventId/reports" element={<ProtectedRoute><EventReports /></ProtectedRoute>} />
      <Route path="/manage/events/:eventId/studio" element={<ProtectedRoute><WebinarStudio /></ProtectedRoute>} />
      <Route path="/manage/events/:eventId/webinar" element={<ProtectedRoute><WebinarStudio /></ProtectedRoute>} />
      <Route path="/manage/events/:eventId/analytics" element={<ProtectedRoute><EventReports /></ProtectedRoute>} />
      <Route path="/manage/events/:eventId/templates" element={<ProtectedRoute><FormTemplateGallery /></ProtectedRoute>} />
      <Route path="/manage/events/:eventId/form-builder" element={<ProtectedRoute><SmartFormBuilder /></ProtectedRoute>} />
      <Route path="/manage/events/branding" element={<ProtectedRoute><CreatorBranding /></ProtectedRoute>} />
      <Route path="/manage/events/forms/templates" element={<ProtectedRoute><FormTemplateGallery /></ProtectedRoute>} />
      <Route path="/webinar/:eventId/studio" element={<ProtectedRoute><WebinarStudio /></ProtectedRoute>} />
      <Route path="/webinar/:eventId/join" element={<PublicLayout><WebinarJoin /></PublicLayout>} />
      <Route path="/webinar/:eventId/room" element={<WebinarRoom />} />
      <Route path="/watch/:eventId" element={<PublicLayout><EvergreenPlayer /></PublicLayout>} />
      <Route path="/event/:slug" element={<PublicLayout><PublicEventPage /></PublicLayout>} />
      <Route path="/my-events" element={<PublicLayout><MyEventsPage /></PublicLayout>} />
      <Route path="/my-dashboard" element={<PublicLayout><MyDashboardPage /></PublicLayout>} />
      <Route path="/event-calendar" element={<PublicLayout><EventCalendarView /></PublicLayout>} />

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={
        <ProtectedRoute>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/volunteers" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/admin/platform" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/admin" element={<AdminLogin />} />
      
      {/* Volunteer Portal Routes */}
      <Route path="/volunteer" element={<VolunteerPortalPage />} />
      <Route path="/volunteer/register" element={<VolunteerRegisterPage />} />
      <Route path="/volunteer/login" element={<VolunteerLoginPage />} />
      <Route path="/volunteer/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/volunteer/reset-password" element={<ResetPasswordPage />} />
      <Route path="/volunteer/dashboard" element={<VolunteerDashboardPage />} />
      <Route path="/volunteer/leaderboard" element={<VolunteerLeaderboardPage />} />
      <Route path="/volunteer/opportunity/:opportunityId" element={<OpportunityDetailPage />} />
      <Route path="/volunteer/training" element={<TrainingHubPage />} />
      <Route path="/volunteer/training/:moduleId" element={<TrainingHubPage />} />
      
      {/* Organization Routes - redirect to unified partner portal */}
      <Route path="/volunteer/org-register" element={<Navigate to="/partner/register" replace />} />
      <Route path="/volunteer/org-login" element={<Navigate to="/partner/login" replace />} />
      <Route path="/volunteer/org-dashboard" element={<Navigate to="/partner/dashboard" replace />} />

      {/* Unified Partner Portal */}
      <Route path="/partner/login" element={<PartnerLoginPage />} />
      <Route path="/partner/register" element={<PartnerRegisterPage />} />
      <Route path="/partner/dashboard" element={<UnifiedPartnerDashboard />} />
      <Route path="/partner/subscription" element={<SubscriptionPage />} />
      <Route path="/partner/subscription/success" element={<SubscriptionPage />} />

      {/* Corporate Team Routes - redirect to unified partner portal */}
      <Route path="/corporate/register" element={<Navigate to="/partner/register" replace />} />
      <Route path="/corporate/login" element={<Navigate to="/partner/login" replace />} />
      <Route path="/corporate/dashboard" element={<Navigate to="/partner/dashboard" replace />} />
      
      {/* 404 Fallback */}
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="/email-preferences" element={<EmailPreferencesPage />} />
      <Route path="*" element={<PublicLayout><NotFoundPage /></PublicLayout>} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <AppRouter />
        <Toaster position="top-right" duration={7000} />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
